import React from 'react';
import classNames from 'classnames';

import type { AnimationItem, AnimationSegment, RendererType } from 'lottie-web';
import lottie from 'lottie-web';

type Props = {
    path?: string; // path of lottie asset, usually it's a URL
    animationData?: unknown; // your own JSON data for the lottie asset
    className?: string;
    renderer?: RendererType; // 'svg'
    name?: string; // name of the lottie node that is created, useful for debugging
    autoplay?: boolean; // if true, plays when component renders
    loop?: boolean;
    playOnHover?: boolean; // automatically plays when hovering on this component
    play?: boolean; // controlled property
    playSegments?: AnimationSegment; // plays specific frames of the animation, where frames are [animationStart, animationEnd]
    onClick?: VoidFunction;
    initialFrame?: 'middle' | number; // set initial frame position ('middle' or specific frame number)
    speed?: number; // playback speed multiplier (default 1)
};

const LottieAsset: React.FC<Props> = React.memo(function Lottie({
    path = '',
    animationData,
    className = '',
    renderer = 'svg',
    name,
    autoplay = false,
    loop = false,
    play = false,
    playOnHover = false,
    playSegments = null,
    onClick,
    initialFrame = 0,
    speed = 1,
}) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const animationInstanceRef = React.useRef<AnimationItem | undefined>(undefined);
    const [segmentValues, setSegmentValues] = React.useState<AnimationSegment>([-1, -1]);

    React.useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const lottieEl = containerRef.current;
        animationInstanceRef.current = lottie.loadAnimation({
            container: lottieEl,
            path,
            autoplay,
            renderer,
            loop,
            name,
            animationData,
            rendererSettings: {
                // @ts-ignore
                runExpressions: false,
            },
        });

        const animation = animationInstanceRef.current;

        if (speed !== 1) {
            animation.setSpeed(speed);
        }

        if (initialFrame !== 0) {
            const seekToInitialFrame = () => {
                const targetFrame =
                    initialFrame === 'middle'
                        ? Math.floor(animation.totalFrames / 2)
                        : initialFrame;
                animation.goToAndStop(targetFrame, true);
                animation.removeEventListener('DOMLoaded', seekToInitialFrame);
            };
            animation.addEventListener('DOMLoaded', seekToInitialFrame);
        }

        return () => animation.destroy();

        // our useEffect hook cannot depend on all the props that'll be used to initialize the animation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [path, loop, containerRef, initialFrame, speed]);

    React.useEffect(() => {
        // segments are [], so need to do deep comparison to see if we need to play the new animation values
        if (Array.isArray(playSegments)) {
            const sameAsPreviousSegment = playSegments.every(
                (value, idx) => segmentValues[idx] === value,
            );
            if (!sameAsPreviousSegment) {
                animationInstanceRef.current?.playSegments(playSegments, true);
                setSegmentValues(playSegments);
            }
        }

        // dependency is missing `segmentValues` which we store in the state 1-cache the comparison
        // disabling eslint because otherwise it'll be infinite looping due to value being an array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playSegments]);

    const handleHover = () => {
        if (playOnHover) {
            animationInstanceRef.current?.play();
        }
    };

    const handleMouseLeave = () => {
        if (playOnHover) {
            animationInstanceRef.current?.pause();
        }
    };

    React.useEffect(() => {
        play ? animationInstanceRef.current?.play() : animationInstanceRef.current?.pause();
    }, [play]);

    return (
        <div
            ref={containerRef}
            className={classNames('lottie-node', className)}
            onMouseEnter={handleHover}
            onMouseLeave={handleMouseLeave}
            onClick={onClick}
        />
    );
});

export default LottieAsset;
